import * as THREE from "three";
import {
	benchRunFootprint,
	doorSwingBox,
	footprintsOverlap,
	heaterFootprint,
	type FootprintMm,
	type InteriorWall,
	wallFaceMm,
	wallRunsAlongX,
} from "../domain/interior";
import { ENGINE_JOIST_H_M, ENGINE_PANEL_T_M } from "./constants";
import { addBox, addCylinder } from "./geometry";
import {
	deriveInteriorDoors,
	deriveInteriorLayoutContext,
	resolveEngineOpenings,
	resolveInteriorLayout,
} from "./interiorLayoutResolver";
import { matBench, matGlass, matMetal } from "./materials";
import type { BuildInput } from "./types";

/** A planning footprint, not a specified wetroom assembly. */
export const SHOWER_SIZE_MM = 900;

export function resolveShowerFootprint(
	input: BuildInput,
): (FootprintMm & { wall: InteriorWall }) | null {
	const context = deriveInteriorLayoutContext(input);
	const room = context?.relaxRoom;
	if (!context || !room) return null;
	const size = SHOWER_SIZE_MM;
	if (room.maxXMm - room.minXMm < size || room.maxZMm - room.minZMm < size)
		return null;
	const layout = resolveInteriorLayout(input, context);
	const obstacles = (layout?.benchRuns ?? [])
		.filter((run) => run.room === "relax")
		.map((run) => benchRunFootprint(run, room));
	// Reserve approach space even for an outward opening entry door.
	obstacles.push(
		...deriveInteriorDoors(input, room, context.envelope).map((door) =>
			doorSwingBox(door, room, door.swingsIntoRoom ? door.widthMm : 600),
		),
	);
	const openings = resolveEngineOpenings(input);
	// ponytail: try three positions per wall; add authored placement when free dragging is needed.
	for (const wall of ["left", "right", "back", "front"] as const) {
		if (context.room.glazedWalls?.includes(wall)) continue;
		const alongX = wallRunsAlongX(wall);
		const alongMin = alongX ? room.minXMm : room.minZMm;
		const alongMax = alongX ? room.maxXMm : room.maxZMm;
		const acrossMin =
			wall === "left"
				? room.minXMm
				: wall === "right"
					? room.maxXMm - size
					: wall === "back"
						? room.minZMm
						: room.maxZMm - size;
		for (const alongMinBox of [
			alongMax - size,
			alongMin,
			(alongMin + alongMax - size) / 2,
		]) {
			const box = alongX
				? {
						minXMm: alongMinBox,
						maxXMm: alongMinBox + size,
						minZMm: acrossMin,
						maxZMm: acrossMin + size,
					}
				: {
						minXMm: acrossMin,
						maxXMm: acrossMin + size,
						minZMm: alongMinBox,
						maxZMm: alongMinBox + size,
					};
			const onDivider =
				(wall === "back" && room.minZMm > context.envelope.minZMm) ||
				(wall === "front" && room.maxZMm < context.envelope.maxZMm);
			const mountAlong = alongMinBox + size / 2;
			const windowOnMountWall = openings.some((opening) => {
				if (
					opening.wall !== (onDivider ? "divider" : wall) ||
					opening.type !== "window"
				)
					return false;
				const origin =
					(alongX ? input.dimensions.width : input.dimensions.length) * 500;
				return (
					mountAlong + 120 > opening.xMm - origin &&
					mountAlong - 120 < opening.xMm - origin + opening.widthMm
				);
			});
			if (
				!windowOnMountWall &&
				!obstacles.some((obstacle) => footprintsOverlap(box, obstacle))
			)
				return { ...box, wall };
		}
	}
	return null;
}

export function resolveSaunaLampPlacement(
	input: BuildInput,
): { wall: InteriorWall; x: number; z: number } | null {
	if (input.thermalInputs.saunaLighting === "none") return null;
	const context = deriveInteriorLayoutContext(input);
	if (!context) return null;
	const room = context.room;
	const layout = resolveInteriorLayout(input, context);
	const heater = heaterFootprint(
		layout?.heater ?? context.heater,
		room,
		context.heaterSize,
	);
	const openings = resolveEngineOpenings(input);
	for (const wall of ["left", "right", "back", "front"] as const) {
		if (room.glazedWalls?.includes(wall)) continue;
		const alongX = wallRunsAlongX(wall);
		const min = alongX ? room.minXMm : room.minZMm;
		const max = alongX ? room.maxXMm : room.maxZMm;
		for (const along of [(min + max) / 2, max - 250, min + 250]) {
			const x = alongX ? along : wallFaceMm(room, wall);
			const z = alongX ? wallFaceMm(room, wall) : along;
			if (
				x > heater.minXMm - 500 &&
				x < heater.maxXMm + 500 &&
				z > heater.minZMm - 500 &&
				z < heater.maxZMm + 500
			)
				continue;
			const onDivider =
				(wall === "back" && room.minZMm > context.envelope.minZMm) ||
				(wall === "front" && room.maxZMm < context.envelope.maxZMm);
			const blocked = openings.some((opening) => {
				if (opening.wall !== (onDivider ? "divider" : wall)) return false;
				const origin = alongX
					? input.dimensions.width * 500
					: input.dimensions.length * 500;
				return (
					along + 100 > opening.xMm - origin &&
					along - 100 < opening.xMm - origin + opening.widthMm
				);
			});
			if (!blocked) return { wall, x: x / 1000, z: z / 1000 };
		}
	}
	return null;
}

export function addInteriorFixtures(
	group: THREE.Group,
	input: BuildInput,
): THREE.Group {
	const floorY = ENGINE_JOIST_H_M + ENGINE_PANEL_T_M;
	const lamp = resolveSaunaLampPlacement(input);
	if (lamp) {
		const fixture = new THREE.Group();
		fixture.name = "detail/sauna-lamp";
		fixture.position.set(lamp.x, floorY + 1.35, lamp.z);
		fixture.rotation.y = {
			back: 0,
			front: Math.PI,
			left: Math.PI / 2,
			right: -Math.PI / 2,
		}[lamp.wall];
		group.add(fixture);
		addBox(0.18, 0.27, 0.025, matBench, 0, 0, 0.013, fixture).name =
			"detail/sauna-lamp/back";
		const lens = new THREE.MeshStandardMaterial({
			color: "#fff1cd",
			roughness: 0.65,
			emissive: "#ffbd62",
			emissiveIntensity: 0.65,
		});
		addBox(0.14, 0.21, 0.035, lens, 0, 0, 0.044, fixture).name =
			"detail/sauna-lamp/lens";
		for (let index = 0; index < 5; index++) {
			addBox(
				0.18,
				0.018,
				0.035,
				matBench,
				0,
				-0.1 + index * 0.05,
				0.075,
				fixture,
			).name = `detail/sauna-lamp/slat-${index}`;
		}
	}
	const shower = input.thermalInputs.includesShower
		? resolveShowerFootprint(input)
		: null;
	if (shower) {
		const fixture = new THREE.Group();
		fixture.name = "detail/shower";
		fixture.position.set(
			(shower.wall === "left"
				? shower.minXMm
				: shower.wall === "right"
					? shower.maxXMm
					: (shower.minXMm + shower.maxXMm) / 2) / 1000,
			floorY,
			(shower.wall === "back"
				? shower.minZMm
				: shower.wall === "front"
					? shower.maxZMm
					: (shower.minZMm + shower.maxZMm) / 2) / 1000,
		);
		fixture.rotation.y = {
			back: 0,
			front: Math.PI,
			left: Math.PI / 2,
			right: -Math.PI / 2,
		}[shower.wall];
		group.add(fixture);
		// ponytail: a generic 90 cm shower symbol; specify waterproofing/drainage separately.
		const wallHeight =
			(deriveInteriorLayoutContext(input)?.room.wallHeightMm ?? 2100) / 1000;
		const headY = Math.min(2.025, wallHeight - 0.1);
		const screenH = Math.min(1.85, wallHeight - 0.15);
		const tray = new THREE.MeshStandardMaterial({
			color: "#eeece5",
			roughness: 0.65,
		});
		addBox(0.9, 0.025, 0.9, tray, 0, 0.013, 0.45, fixture).name =
			"detail/shower/tray";
		addCylinder(0.045, 0.003, matMetal, 0, 0.027, 0.45, fixture).name =
			"detail/shower/drain";
		addBox(
			0.012,
			headY - 0.775,
			0.012,
			matMetal,
			0,
			(headY + 0.775) / 2,
			0.055,
			fixture,
		).name = "detail/shower/rail";
		addBox(0.23, 0.05, 0.065, matMetal, 0, 1.05, 0.055, fixture).name =
			"detail/shower/mixer";
		addBox(0.014, 0.014, 0.32, matMetal, 0, headY, 0.2, fixture).name =
			"detail/shower/arm";
		addCylinder(0.105, 0.018, matMetal, 0, headY - 0.02, 0.35, fixture).name =
			"detail/shower/head";
		for (const side of [-1, 1]) {
			addBox(
				0.006,
				screenH,
				0.9,
				matGlass,
				side * 0.447,
				screenH / 2 + 0.025,
				0.45,
				fixture,
			).name = `detail/shower/screen-${side}`;
			addBox(
				0.012,
				screenH,
				0.015,
				matMetal,
				side * 0.447,
				screenH / 2 + 0.025,
				0.89,
				fixture,
			).name = `detail/shower/edge-${side}`;
		}
	}
	return group;
}
